<template>
  <div class="orders-page">
    <OrderForm 
      :visible="showForm" 
      :order-id="editingOrder"
      @close="handleOrderUpdated"
      @order-created="handleOrderCreated"
      @order-updated="handleOrderUpdated"
    />
    <CustomerTraffic
      :visible="showTraffic" 
      :user-authority="userAuthority"
      @close="handleTrafficFormClosed"
    />
    <DeliverySheet
      :visible="showDeliverySheet"
      @close="handleDeliverySheetClosed"
    />
    <SalesStatList
      :visible="showSalesStatList"
      :user-authority="userAuthority"
      @close="handleSalesStatListClosed"
    />
    <OptionsManage
      :visible="showOptionsManage"
      :user-authority="userAuthority"
      @close="handleOptionsManageClosed"
    />
    <div class="orders-list">
      <div class="search-wrapper">
        <label class="undelivered-checkbox-wrapper">
          <input type="checkbox" v-model="showUndeliveredOnly" class="undelivered-checkbox">
          未交貨
        </label>
        <input type="text" v-model="searchQuery" placeholder="搜尋訂單編號、日期、姓名、地址、電話、備註..." class="search-input">
      </div>
      <div class="table-wrapper">
        <table :class="{ 'loading': isLoading }">
          <thead>
            <tr>
              <th>交貨</th>
              <th>訂單編號</th>
              <th>顧客姓名</th>
              <th>銷售員</th>
              <th>訂貨日期</th>
              <th>交貨日期</th>
              <th>聯絡電話</th>
              <th>地址</th>
              <th>來源</th>
              <th>總計</th>
              <th>備註</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="order in orders" :key="order.id">
              <!-- Order row -->
              <tr 
                @click="toggleOrderItems(order.id)"
                :class="{ 'expanded': expandedOrderId === order.id }"
                class="order-row"
                :data-cy="`order-row-${order.id}`"
              >
                <td>
                  <input 
                    type="checkbox" 
                    :checked="order.delivered"
                    @click.stop="toggleDeliveredStatus(order, $event)"
                    :disabled="userAuthority > 1"
                    class="delivery-checkbox"
                  >
                </td>
                <td class="order-id-column">{{ order.id }}</td>
                <td class="name-column">{{ order.name }}</td>
                <td class="name-column">{{ order.sales_name }}</td>
                <td class="date-column">{{ formatDate(order.order_date) }}</td>
                <td class="date-column">{{ formatDate(order.deliver_date) }}</td>
                <td>{{ order.phone }}</td>
                <td>{{ order.addr }}</td>
                <td>{{ order.intro_by }}</td>
                <td class="price-column">{{ formatCurrency(order.sum) }}</td>
                <td class="memo-column">{{ order.memo }}</td>
              </tr>
              <!-- Items, Disposal and Payment rows -->
              <tr v-if="expandedOrderId === order.id" :key="order.id + '-details'" class="details-row">
                <td colspan="11">
                  <div class="details-container">
                    <!-- 基本資料 (Basic Information) -->
                    <div class="section basic-info">
                      <h3>基本資料</h3>
                      <div class="info-grid">
                        <div class="info-item">
                          <span class="info-label">訂單編號:</span>
                          <span class="info-value">{{ order.id }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">顧客姓名:</span>
                          <span class="info-value">{{ order.name }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">銷售員:</span>
                          <span class="info-value">{{ order.sales_name }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">訂貨日期:</span>
                          <span class="info-value">{{ formatDate(order.order_date) }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">交貨日期:</span>
                          <span class="info-value">{{ formatDate(order.deliver_date) }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">來源:</span>
                          <span class="info-value">{{ order.intro_by || 'N/A' }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">聯絡電話:</span>
                          <span class="info-value">{{ order.phone }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">地址:</span>
                          <span class="info-value">{{ order.addr }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">備註:</span>
                          <span class="memo-column">{{ order.memo }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">聯絡電話2:</span>
                          <span class="info-value">{{ order.phone2 }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">地址2:</span>
                          <span class="info-value">{{ order.addr2 }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">發票:</span>
                          <span class="info-value">{{ order.need_invoice ? '需要' : '不需要' }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">交付:</span>
                          <span class="info-value">{{ order.delivered ? '已交付' : '未交付' }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">節假日調整:</span>
                          <span class="info-value">{{ order.share_day ? '假日' : '' }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">發票號碼:</span>
                          <span class="info-value">{{ order.invoice }}</span>
                        </div>
                        <div class="action-buttons" v-if="userAuthority <= 1">
                          <button class="delete-btn" @click.stop="confirmDeleteOrder(order)">刪除訂單</button>
                          <button class="edit-btn" @click.stop="editOrder(order)">編輯訂單</button>
                        </div>
                      </div>
                    </div>

                    <!-- 訂購商品資訊 (Order Items) -->
                    <div v-if="order.items && order.items.length > 0" class="section">
                      <h3>訂購商品資訊</h3>
                      <table class="details-table">
                        <thead>
                          <tr>
                            <th>尺寸</th>
                            <th>品項</th>
                            <th>顏色</th>
                            <th>單價</th>
                            <th>特殊加價</th>
                            <th>折扣</th>
                            <th>數量</th>
                            <th>小計</th>
                            <th>備註</th>
                            <th>司機</th>
                            <th>運費</th>
                            <th>運費備註</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in order.items" :key="item.id" class="detail-row">
                            <td class="size-column">{{ item.size + (item.customized ? '(特殊)' : '') }}</td>
                            <td class="name-column">{{ item.product_name }}</td>
                            <td>{{ item.color }}</td>
                            <td class="price-column">{{ formatCurrency(item.price) }}</td>                            
                            <td class="extra-fee-column">{{ formatCurrency(item.extra_fee) }}</td>
                            <td class="discount-column">{{ item.discount }}</td>
                            <td class="quantity-column">{{ item.quantity }}</td>
                            <td>{{ formatCurrency(item.sum) }}</td>
                            <td>{{ item.extra_memo }}</td>
                            <td>{{ item.driver_name }}</td>
                            <td v-if="item.ship_extra_fee" class="ship-fee-column">{{ formatCurrency(item.ship_sum) }} ({{ item.ship_fee }} + {{ item.ship_extra_fee }})</td>
                            <td v-else class="ship-fee-column">{{ formatCurrency(item.ship_sum) }}</td>
                            <td>{{ item.ship_extra_memo }}</td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr><td colspan="12" style="padding:10px"></td></tr>
                          <tr>
                            <td colspan="7" class="total-label">訂單總計</td>
                            <td>{{ formatCurrency(order.items.reduce((total, item) => total + item.sum, 0)) }}</td>
                          </tr>
                          <tr>
                            <td colspan="7" class="total-label">運費總計</td>
                            <td>{{ formatCurrency(order.items.reduce((total, item) => total + (item.ship_sum ? item.ship_sum : 0), 0)) }}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    <!-- Disposals and Payments Container -->
                    <div class="sections-container">
                      <!-- Disposals -->
                      <div class="item-section">
                        <h3>回收處理</h3>
                        <table class="details-table">
                          <thead>
                            <tr>
                              <th>數量</th>
                              <th>司機</th>
                              <th>運費</th>
                              <th>回收費用</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="disposal in order.disposals" :key="disposal.id" class="detail-row">
                              <td class="quantity-column">{{ disposal.quantity }}</td>
                              <td class="name-column">{{ disposal.driver_name }}</td>
                              <td class="price-column">{{ formatCurrency(disposal.ship_sum) }}</td>
                              <td v-if="disposal.extra_fee" class="price-column">{{ formatCurrency(disposal.sum) }} ({{ disposal.fee }} + {{ disposal.extra_fee + "胖嘟嘟" }})</td>
                              <td v-else class="price-column">{{ formatCurrency(disposal.sum) }}</td>
                            </tr>
                            <tr v-if="!order.disposals || order.disposals.length == 0">
                              <td colspan="2"></td>
                              <td>無資料</td>
                            </tr>
                          </tbody>
                          <tfoot v-if="order.disposals && order.disposals.length > 0">
                            <tr>
                              <td colspan="4" style="padding:10px"></td>
                            </tr>
                            <tr>
                              <td colspan="3" class="total-label">回收費用</td>
                              <td>{{formatCurrency(order.disposals ? order.disposals.reduce((total, disposal) => total + disposal.sum, 0) : 0) }}</td>
                            </tr>
                            <tr>
                              <td colspan="3" class="total-label">運費</td>
                              <td>{{formatCurrency(order.disposals ? order.disposals.reduce((total, disposal) => total + (disposal.ship_sum ? disposal.ship_sum : 0), 0) : 0) }}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      <!-- Payments -->
                      <div class="item-section">
                        <h3>付款資訊</h3>
                        <table class="details-table">
                          <thead>
                            <tr>
                              <th>付款日期</th>
                              <th>付款方式</th>
                              <th>金額</th>
                              <th>發票號碼</th>
                              <th>刷卡序號</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="payment in order.payments" :key="payment.id" class="detail-row">
                              <td class="date-column">{{ formatDate(payment.pay_date) }}</td>
                              <td class="name-column">{{ payment.method_name }}</td>
                              <td class="price-column">{{ formatCurrency(payment.amount) }}</td>
                              <td>{{ payment.invoice }}</td>
                              <td>{{ payment.series }}</td>
                            </tr>
                            <tr v-if="!order.payments || order.payments.length == 0">
                              <td colspan="2"></td>
                              <td>無資料</td>
                            </tr>
                          </tbody>
                          <tfoot v-if="order.payments && order.payments.length > 0">
                            <tr><td colspan="5" style="padding:10px"></td></tr>
                            <tr>
                              <td colspan="2" class="total-label">已付</td>
                              <td>{{ formatCurrency(order.payments ? order.payments.reduce((total, payment) => total + payment.amount, 0) : 0) }}</td>
                            </tr>
                             <tr>
                              <td colspan="2" class="total-label">未付款</td>
                              <td>{{ formatCurrency(order.payments ? order.items.reduce((total, item) => total + item.sum, 0) - order.payments.reduce((total, payment) => total + payment.amount, 0) : 0) }}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>

                    <div v-if="!order.items" class="loading-details">
                      讀取訂單中，太久請重新整理....
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <footer>
      <button class="func-btn" @click="showForm = true" v-if="userAuthority <= 1">+新增訂單</button>
      <button class="func-btn" @click="showDeliverySheet = true">出貨單</button>
      <button class="func-btn" @click="showTraffic = true">客流量</button>
      <button class="func-btn" @click="showSalesStatList = true">業務員訂單查詢</button>
      <button class="func-btn" @click="openStatChart">年度業績統計</button>
      <button class="func-btn" @click="showOptionsManage = true" v-if="userAuthority <= 1">選項管理</button>
    </footer>
  </div>
</template>

<script>
import CustomerTraffic from './CustomerTraffic.vue';
import OrderForm from './OrderForm.vue';
import DeliverySheet from './DeliverySheet.vue';
import SalesStatList from './SalesStatList.vue';
import OptionsManage from './OptionsManage.vue';

export default {
  name: 'OrdersList',
  components: {
    OrderForm,
    CustomerTraffic,
    DeliverySheet,
    SalesStatList,
    OptionsManage,
  },
  props: {
    userAuthority: {
      type: Number,
      default: 2,
    },
  },
  data() {
    return {
      orders: [],
      expandedOrderId: null,
      showForm: false,
      showTraffic: false,
      showDeliverySheet: false,
      showSalesStatList: false,
      showOptionsManage: false,
      editingOrder: null, // Can be an order ID
      isLoading: false,
      hasMore: true,
      page: 1,
      searchQuery: '',
      showUndeliveredOnly: false, // New data property for the checkbox
      debouncedSearch: null,
      throttledHandleScroll: null, // New data property for throttled scroll handler
    };
  },
  watch: {
    searchQuery() {
      this.debouncedSearch();
    },
    showUndeliveredOnly() { // Watch for changes in the new checkbox
      this.debouncedSearch(); // Trigger a new search when it changes
    }
  },
  created() {
    this.debouncedSearch = this.debounce(() => {
      this.page = 1;
      this.hasMore = true;
      this.fetchOrders(true); // Pass true to indicate a new search
    }, 500);
    this.throttledHandleScroll = this.throttle(this.handleScroll, 200); // Throttle scroll events to every 200ms
    this.fetchOrders(true); // Initial load should also be a new search
  },
  mounted() {
    const tableWrapper = this.$el.querySelector('.table-wrapper');
    if (tableWrapper) {
      tableWrapper.addEventListener('scroll', this.throttledHandleScroll);
    }
  },
  beforeUnmount() {
    const tableWrapper = this.$el.querySelector('.table-wrapper');
    if (tableWrapper) {
      tableWrapper.removeEventListener('scroll', this.throttledHandleScroll);
    }
  },
  methods: {
    async fetchOrders(isNewSearch = false) { // Add a flag for new search
      if (this.isLoading && !isNewSearch) return; // Prevent multiple fetches for infinite scroll
      if (isNewSearch) {
        this.isLoading = true; // Set loading true immediately for new search
        this.page = 1;
        this.hasMore = true;
        this.expandedOrderId = null;
      } else if (this.isLoading || !this.hasMore) {
        return; // For infinite scroll, only proceed if not loading and has more
      }

      try {
        let url = `/api/orders?page=${this.page}`;
        if (this.searchQuery) {
          url += `&search=${this.searchQuery}`;
        }
        if (this.showUndeliveredOnly) {
          url += `&undelivered_only=true`;
        }
        const response = await fetch(url);
        if (!response.ok) throw new Error('Network response was not ok');
        
        const data = await response.json();
        
        if (isNewSearch) {
          this.orders = data.orders; // Replace orders for search, when we clear the search box, this.orders will replace by page 1, which reset the list.
        } else {
          this.orders = [...this.orders, ...data.orders]; // Append for infinite scroll
        }
        this.hasMore = data.has_next;
        if (this.hasMore) {
          this.page++;
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        this.isLoading = false;
      }
    },
    handleOrderCreated() {
      this.showForm = false;
      this.editingOrder = null;
      this.fetchOrders(true);
    },
    handleOrderUpdated() {
      this.showForm = false;
      this.editingOrder = null;
      // Update order details.
      this.fetchOrderDetailAndUpdate(this.expandedOrderId)
    },
    handleTrafficFormClosed() {
      this.showTraffic = false;
    },
    handleDeliverySheetClosed() {
      this.showDeliverySheet = false;
    },
    handleSalesStatListClosed() {
      this.showSalesStatList = false;
      this.fetchOrders(true); // Refresh orders when SalesStatList is closed
    },
    handleOptionsManageClosed() {
      this.showOptionsManage = false;
    },
    async toggleDeliveredStatus(order, event) {
      // If the user is trying to mark as delivered and deliver_date is not set
      if (!order.delivered && !order.deliver_date) {
        alert('請先填寫交貨日期');
        if (event) {
          event.preventDefault(); // Prevent the checkbox from changing its state
        }
        return; // Prevent updating status
      }

      try {
        const newStatus = !order.delivered;
        const response = await fetch(`/api/orders/${order.id}/delivered`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ delivered: newStatus })
        });
        if (!response.ok) throw new Error('Failed to update delivery status');
        const data = await response.json();
        order.delivered = data.delivered;
      } catch (error) {
        console.error('Error updating delivery status:', error);
        alert('更新交貨狀態失敗');
      }
    },
    confirmDeleteOrder(order) {
      if (confirm(`確定要刪除訂單 #${order.id} 嗎？此操作無法復原。`)) {
        this.deleteOrder(order.id);
      }
    },
    async deleteOrder(orderId) {
      try {
        const response = await fetch(`/api/orders/${orderId}`, { method: 'DELETE' });
        if (!response.ok) throw new Error('Failed to delete order');
        
        // Remove the order from the local list instead of a full refetch
        this.orders = this.orders.filter(o => o.id !== orderId);
      } catch (error) {
        console.error('Error deleting order:', error);
        alert('刪除訂單時發生錯誤');
      }
    },
    editOrder(order) {
      this.editingOrder = order.id;
      this.showForm = true;
    },
    toggleOrderItems(orderId) {
      if (this.expandedOrderId === orderId) {
        this.expandedOrderId = null;
      } else {
        this.expandedOrderId = orderId;
        this.fetchOrderDetailAndUpdate(orderId);
      }
    },
    async fetchOrderDetailAndUpdate(orderId) {
      const order = this.orders.find(o => o.id === orderId);
      if (order) {
        try {
          const response = await fetch(`/api/orders/${orderId}`);
          if (!response.ok) throw new Error('Failed to fetch order details');
          const data = await response.json();
          const order_idx = this.orders.findIndex(o => o.id === orderId);
          this.orders[order_idx] = data;

        } catch (error) {
          console.error('Error fetching order details:', error);
          alert('Failed to load order details. Please try again.');
        }
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    debounce(func, delay) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), delay);
      };
    },
    throttle(func, delay) {
      let timeoutId = null;
      let lastArgs = null;
      let lastThis = null;

      return function(...args) {
        lastArgs = args;
        lastThis = this;

        if (!timeoutId) {
          timeoutId = setTimeout(() => {
            func.apply(lastThis, lastArgs);
            timeoutId = null;
            lastArgs = null;
            lastThis = null;
          }, delay);
        }
      };
    },
    handleScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollHeight - scrollTop <= clientHeight + 100 && !this.isLoading && this.hasMore) {
        this.fetchOrders();
      }
    },
    openStatChart() {
      window.open('/annual-stat-chart', '_blank');
    },
    formatCurrency(val) {
      const v = val || 0;
      return new Intl.NumberFormat('zh-TW', {
        style: 'currency',
        currency: 'TWD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(v);
    },
  }
}
</script>

<style scoped>
.delivery-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

/* Checkbox styling */
.delivery-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #3b82f6;
  border-radius: 0.25rem;
  border: 1px solid #cbd5e1;
}

.delivery-checkbox:checked {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

.delivery-checkbox:focus {
  outline: 2px solid #93c5fd;
  outline-offset: 2px;
}

.basic-info {
  margin-bottom: 2rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-top: 1rem;
}

.info-item {
  display: flex;
  line-height: 1.6;
}

.info-label {
  font-weight: 600;
  color: #495057;
  min-width: 80px;
  margin-right: 0.5rem;
}

.info-value {
  color: #212529;
  word-break: break-word;
}

.action-buttons {
  grid-column: 3;
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
  padding: 10px 0;
}

.edit-btn, .delete-btn {
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 8px;
  font-weight: 500;
  transition: background-color 0.3s;
}

.edit-btn {
  background-color: #4CAF50;
  color: white;
}

.edit-btn:hover {
  background-color: #45a049;
}

.delete-btn {
  background-color: #f44336;
  color: white;
}

.delete-btn:hover {
  background-color: #d32f2f;
}

.edit-btn:active {
  background-color: #3d8b40;
}

.orders-page {
  position: relative;
  min-height: 100vh;
}

.orders-list {
  width: fit-content; /* Ensure it takes full width */
  min-width: 1400;
  padding: 20px;
  margin: 0 auto;
  margin-bottom: 60px; /* Add space for footer */
}

.table-wrapper {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: auto;
  max-height: calc(100vh - 200px);
  position: relative;
}

table {
  min-width: 600px;
  border-collapse: collapse;
  transition: opacity 0.5s ease-in-out, transform 0.5s ease-in-out; /* Add transition */
}

table.loading {
  opacity: 0.5; /* Reduce opacity when loading */
  transform: translateY(10px); /* Slight movement */
}

th, td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
}

.order-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.order-row:hover {
  background-color: #f5f5f5;
}

.order-row.expanded {
  background-color: #e3f2fd;
}

.details-row {
  background-color: #fafafa;
}

.details-container {
  padding: 20px;
}

.sections-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.item-section {
  width: fit-content;  
}

.section {
  margin-bottom: 30px;
}

.section h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.1em;
}

.details-table {
  width: 100%;
  margin-top: 10px;
  background-color: white;
  border: 1px solid #eee;
}

.details-table th {
  background-color: #f0f0f0;
  font-size: 1em;
  padding: 10px;
  border-bottom: 2px solid #ddd;
}

.details-table td {
  padding: 8px 10px;
  font-size: 1em;
  border-bottom: 1px solid #eee;
}

.detail-row:hover {
  background-color: #f8f9fa;
}

.loading-details {
  text-align: center;
  padding: 20px;
  color: #666;
}

.ship-details {
  font-size: 0.85em;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.ship-details div {
  margin: 4px 0;
}

.ship-details strong {
  color: #2c3e50;
}

.no-ship {
  color: #666;
  font-style: italic;
  font-size: 0.85em;
}

.order-id-column {
  min-width: 70px;
  max-width: 100px;
}

.total-label {
  text-align: right;
}

/* Quantity column styling */
.quantity-column {
  width: 32px;  /* Fixed width to fit 2 digits */
  min-width: 32px;
  max-width: 32px;
  text-align: center;
}

.name-column {
  min-width: 70px;
  width: fit-content;
}

.memo-column {
  min-width: 100px;
  max-width: 200px;
  word-break: break-all; /* or overflow-wrap: break-word; */
}

.size-column {
  min-width: 60px;
  width: fit-content;
}

.price-column {
  min-width: 50px;
  max-width: 70px;
  text-align: left;
}

.date-column {
  min-width: 80px;
  max-width: 80px;
}

@media (max-width: 768px) {
  .orders-list {
    padding: 10px;
  }
  
  th, td {
    padding: 8px;
  }

  .section h3 {
    font-size: 1em;
  }
  
  .details-table th,
  .details-table td {
    padding: 6px 8px;
    font-size: 0.85em;
  }

  .ship-details {
    font-size: 0.8em;
    padding: 4px;
  }
  
  .ship-details div {
    margin: 2px 0;
  }
}

footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px;
  background-color: white;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  text-align: center;
  z-index: 100;
}

.func-btn {
  background-color: #2c3e50;
  color: white;
  border: none;
  padding: 12px 12px;
  margin: 2px;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.func-btn:hover {
  background-color: #1a252f;
}

.search-wrapper {
  margin-bottom: 1rem;
  display: flex; /* Use flexbox to arrange children horizontally */
  align-items: center; /* Vertically align items in the center */
  gap: 10px; /* Add some space between the checkbox and the search input */
  justify-content: flex-start; /* Align items to the start of the flex container */
  flex-wrap: nowrap; /* Prevent items from wrapping to the next line */
  width: 100%; /* Ensure the wrapper takes full available width */
}

.search-input {
  flex-grow: 1; /* Allow the search input to take up remaining space */
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  min-width: 150px; /* Ensure it has a minimum width to prevent collapsing */
}

.undelivered-checkbox-wrapper {
  display: flex;
  align-items: center;
  margin: 0; /* Ensure no external margins are affecting layout */
  font-size: 0.9rem;
  color: #555;
  white-space: nowrap; /* Prevent text from wrapping */
  flex-shrink: 0; /* Prevent the checkbox wrapper from shrinking */
}

.undelivered-checkbox {
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #3b82f6;
}
</style>
